import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { OrgWidgetData } from '../../models/org-widget.models';
import { OrgEntityColorsService, OrgEntityType } from '../../services/org-entity-colors.service';

@Component({
    selector: 'shared-org-overview-widget',
    imports: [CommonModule, MatIconModule],
    template: `
        <div class="overview-container" *ngIf="data">
            <div class="overview-grid">
                <div
                    class="stat-card clickable"
                    *ngFor="let stat of stats; trackBy: trackByRoute"
                    (click)="onStatClick(stat)"
                >
                    <mat-icon class="stat-icon" [style.color]="colorsService.getColor(stat.entityType)">{{
                        stat.icon
                    }}</mat-icon>
                    <div class="stat-info">
                        <span class="stat-value">{{ stat.value }}</span>
                        <span class="stat-label">{{ stat.label }}</span>
                    </div>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .overview-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
                gap: 12px;
            }

            .stat-card {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 12px;
                border-radius: 8px;
                background: rgba(0, 0, 0, 0.02);
                transition: background 0.2s ease;
                cursor: pointer;

                &:hover {
                    background: rgba(0, 0, 0, 0.06);
                }

                &:active {
                    background: rgba(0, 0, 0, 0.08);
                }
            }

            .stat-icon {
                font-size: 28px;
                width: 28px;
                height: 28px;
                flex-shrink: 0;
            }

            .stat-info {
                display: flex;
                flex-direction: column;
                min-width: 0;
            }

            .stat-value {
                font-size: 22px;
                font-weight: 600;
                line-height: 1.2;
                color: rgba(0, 0, 0, 0.87);
            }

            /* Wraps instead of ellipsing: "Org structure parts" and "Security Groups" were cut
               to "Org structur..." / "Security Gr..." at desktop widths (ISS-023). */
            .stat-label {
                font-size: 12px;
                line-height: 1.25;
                color: rgba(0, 0, 0, 0.54);
                overflow-wrap: break-word;
            }

            /* Dark theme */
            :host-context(.dark-theme) {
                .stat-card {
                    background: rgba(255, 255, 255, 0.04);
                    &:hover {
                        background: rgba(255, 255, 255, 0.1);
                    }
                    &:active {
                        background: rgba(255, 255, 255, 0.14);
                    }
                }
                .stat-value {
                    color: rgba(255, 255, 255, 0.87);
                }
                .stat-label {
                    color: rgba(255, 255, 255, 0.54);
                }
            }

            /* Phones: compact the stat cards + icons so more fit in the widget and the
           section takes less vertical space. Smaller min-column packs 2–3 per row. */
            @media (max-width: 767.98px) {
                /* Four compact tiles a row, number over label, so all seven fit the widget's
                   fixed phone height — three a row put Permissions below the fold (ISS-023). */
                .overview-grid {
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: 6px;
                }
                .stat-card {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 2px;
                    padding: 6px;
                    border-radius: 6px;
                    min-width: 0;
                }
                .stat-icon {
                    font-size: 18px;
                    width: 18px;
                    height: 18px;
                }
                .stat-info {
                    width: 100%;
                }
                .stat-value {
                    font-size: 16px;
                }
                .stat-label {
                    font-size: 10.5px;
                    line-height: 1.2;
                }
            }
        `,
    ],
})
export class OrgOverviewWidgetComponent implements OnChanges {
    @Input() data!: OrgWidgetData;
    @Output() tileClicked = new EventEmitter<OverviewTileClick>();

    /**
     * Built once per data change, never per repaint: a getter that returned a fresh array made the
     * ngFor rebuild every tile on each change detection, so while the dashboard was still loading
     * a click landed on a tile that was already gone and the hover flickered (28 Oct).
     */
    public stats: OverviewStat[] = [];

    constructor(public colorsService: OrgEntityColorsService) {}

    public ngOnChanges(): void {
        this.stats = this.buildStats();
    }

    public trackByRoute(_: number, stat: OverviewStat): string {
        return stat.routeKey;
    }

    /** A staff member's tile opens only what it counted (ISS-022); an org admin's opens the whole list. */
    public onStatClick(stat: OverviewStat): void {
        const scoped = !!this.data?.viewer && !this.data.viewer.isOrgAdmin;
        this.tileClicked.emit({ routeKey: stat.routeKey, onlyKeys: scoped ? stat.keys : undefined });
    }

    private buildStats(): OverviewStat[] {
        if (!this.data) return [];
        const keysOf = (list: { key: string }[] | undefined) => (list || []).map((e) => e.key);
        const permissionKeys = this.uniquePermissionKeys;
        // Order agreed with Jessica (ISS-023, 27 Oct).
        const stats: Omit<OverviewStat, 'keys'>[] = [
            {
                icon: 'people',
                label: 'Staff',
                value: this.data.users?.length || 0,
                entityType: 'users',
                routeKey: 'users',
            },
            { icon: 'school', label: 'Hats', value: this.data.hats?.length || 0, entityType: 'hats', routeKey: 'hats' },
            {
                icon: 'account_tree',
                label: 'Org structure parts',
                value: this.data.positions?.length || 0,
                entityType: 'positions',
                routeKey: 'designer',
            },
            {
                icon: 'policy',
                label: 'Policies',
                value: this.data.policies?.length || 0,
                entityType: 'policies',
                routeKey: 'my-policies',
            },
            {
                icon: 'security',
                label: 'Security Groups',
                value: this.data.securityGroups?.length || 0,
                entityType: 'securityGroups',
                routeKey: 'roles',
            },
            {
                icon: 'extension',
                label: 'Features',
                value: this.data.features?.length || 0,
                entityType: 'features',
                routeKey: 'features',
            },
            {
                icon: 'vpn_key',
                label: 'Permissions',
                value: permissionKeys.length,
                entityType: 'permissions',
                routeKey: 'permissions',
            },
        ];
        const keys: Record<string, string[]> = {
            users: keysOf(this.data.users),
            hats: keysOf(this.data.hats),
            designer: (this.data.positions || []).map((p) => String((p as Record<string, unknown>)['partKey'] ?? '')),
            'my-policies': keysOf(this.data.policies),
            roles: keysOf(this.data.securityGroups),
            features: keysOf(this.data.features),
            permissions: permissionKeys,
        };
        return stats.map((s) => ({ ...s, keys: keys[s.routeKey] || [] }));
    }

    /**
     * Count the permissions security groups give, once each. A permission belongs to its feature
     * (`hasPermission(user, feature, permission)`), so the same key under two features is two
     * permissions — the count the Permissions list shows (ISS-024; keyed by name alone this read
     * 17 against the list's 34 on dev).
     */
    private get uniquePermissionKeys(): string[] {
        const groups = this.data?.securityGroups;
        if (!groups?.length) return [];

        const seen = new Set<string>();
        for (const sg of groups) {
            const featureDetails = (sg.data as Record<string, unknown>)['featureDetails'] as
                | Record<string, { permissions?: string[] }>
                | undefined;
            if (!featureDetails) continue;
            for (const [featureKey, feat] of Object.entries(featureDetails)) {
                if (Array.isArray(feat?.permissions)) {
                    for (const p of feat.permissions) {
                        seen.add(`${featureKey}/${p}`);
                    }
                }
            }
        }
        return [...seen];
    }
}

/** What a tile click asks for: its list, narrowed to `onlyKeys` when the viewer is not an org admin. */
export interface OverviewTileClick {
    routeKey: string;
    onlyKeys?: string[];
}

interface OverviewStat {
    icon: string;
    label: string;
    value: number;
    entityType: OrgEntityType;
    routeKey: string;
    keys: string[];
}
